import { useQuery } from "@tanstack/react-query";
import { leadsApi } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatLeadStatusLabel, formatLeadSubStatusLabel } from "@/lib/crmLeadStyles";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isCrmViewOnlyUser } from "@/utils/crmAccess";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

const JOURNEY_STEPS = [
  { key: "opd_booked", label: "Self booked" },
  { key: "opd_visited", label: "OPD visited" },
  { key: "ipd", label: "IPD / Procedure" },
] as const;

function journeyStepReached(current: string, step: string): boolean {
  const order = ["opd_booked", "opd_visited", "ipd", "closed", "lost", "other"];
  const curIdx = order.indexOf(current);
  const stepIdx = order.indexOf(step);
  if (curIdx < 0 || stepIdx < 0) return false;
  if (current === "lost") return step === "opd_booked";
  return curIdx >= stepIdx;
}

function JourneyTrail({ stage }: { stage: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {JOURNEY_STEPS.map((step, idx) => {
        const done = journeyStepReached(stage, step.key);
        return (
          <span key={step.key} className="inline-flex items-center gap-1">
            {idx > 0 && <span className="text-slate-300">→</span>}
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                done ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-500"
              }`}
            >
              {step.label}
            </span>
          </span>
        );
      })}
      {stage === "lost" && (
        <>
          <span className="text-slate-300">→</span>
          <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800">
            Lost
          </span>
        </>
      )}
    </div>
  );
}

export function SelfBookedLeadsTab({ from, to, hospitalId }: Props) {
  const { user } = useAuth();
  const crmViewOnly = isCrmViewOnlyUser(user);

  const reportParams = { from, to, ...(hospitalId ? { hospitalId } : {}) };

  const query = useQuery({
    queryKey: ["lead-self-booked-report", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getSelfBookedLeadsReport(reportParams);
      return res.data.data;
    },
  });

  const downloadCsv = async () => {
    try {
      const res = await leadsApi.downloadSelfBookedLeadsReport(reportParams);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `self-booked-leads-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Self booked patients</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Patients who booked OPD themselves via the website — either after a campaign WhatsApp
                message or directly on trueartis.com. Filtered by self-book date in the selected range.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadCsv()}
              disabled={crmViewOnly || query.isLoading}
              className={crmViewOnly ? "hidden" : undefined}
            >
              Download CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">Self booked</div>
                  <div className="text-xl font-semibold">{query.data?.totals?.selfBooked ?? 0}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">WhatsApp campaign</div>
                  <div className="text-xl font-semibold">
                    {query.data?.totals?.campaignWhatsApp ?? 0}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">Website direct</div>
                  <div className="text-xl font-semibold">
                    {query.data?.totals?.directWebsite ?? 0}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">OPD visited</div>
                  <div className="text-xl font-semibold">{query.data?.totals?.opdVisited ?? 0}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">IPD / Procedure</div>
                  <div className="text-xl font-semibold">{query.data?.totals?.ipd ?? 0}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium mb-2">By BD (lead owner)</div>
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left">BD</th>
                          <th className="p-2 text-center">Self booked</th>
                          <th className="p-2 text-center">OPD visited</th>
                          <th className="p-2 text-center">IPD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(query.data?.byBd ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-muted-foreground">
                              No self booked leads in this period.
                            </td>
                          </tr>
                        ) : (
                          (query.data?.byBd ?? []).map((row: any) => (
                            <tr key={row.assigneeId} className="border-t">
                              <td className="p-2 font-medium">{row.assigneeName}</td>
                              <td className="p-2 text-center">{row.selfBooked}</td>
                              <td className="p-2 text-center">{row.opdVisited}</td>
                              <td className="p-2 text-center">{row.ipd}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">By booking channel</div>
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-max text-xs w-full">
                      <thead className="bg-gray-50 text-muted-foreground">
                        <tr>
                          <th className="p-2 text-left">Channel</th>
                          <th className="p-2 text-center">Self booked</th>
                          <th className="p-2 text-center">OPD visited</th>
                          <th className="p-2 text-center">IPD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(query.data?.byChannel ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-muted-foreground">
                              No data
                            </td>
                          </tr>
                        ) : (
                          (query.data?.byChannel ?? []).map((row: any) => (
                            <tr key={row.bookingChannel} className="border-t">
                              <td className="p-2 font-medium">{row.bookingChannelLabel}</td>
                              <td className="p-2 text-center">{row.count}</td>
                              <td className="p-2 text-center">{row.opdVisited}</td>
                              <td className="p-2 text-center">{row.ipd}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Patient journey detail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border rounded-md max-h-[520px] overflow-y-auto">
            <table className="min-w-max text-xs w-full">
              <thead className="bg-gray-50 text-muted-foreground sticky top-0">
                <tr>
                  <th className="p-2 text-left">Patient</th>
                  <th className="p-2 text-left">Phone</th>
                  <th className="p-2 text-left">Lead source</th>
                  <th className="p-2 text-left">Booking channel</th>
                  <th className="p-2 text-left">Self booked at</th>
                  <th className="p-2 text-left">Appointment</th>
                  <th className="p-2 text-left">Journey</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">BD</th>
                </tr>
              </thead>
              <tbody>
                {(query.data?.leads ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-4 text-center text-muted-foreground">
                      No self booked patients in this period.
                    </td>
                  </tr>
                ) : (
                  (query.data?.leads ?? []).map((lead: any) => (
                    <tr key={lead.leadId} className="border-t">
                      <td className="p-2 font-medium">{lead.patientName}</td>
                      <td className="p-2">{lead.phone}</td>
                      <td className="p-2">{lead.leadSource}</td>
                      <td className="p-2">
                        <span
                          className={`inline-block rounded px-1.5 py-0.5 font-medium ${
                            lead.bookingChannel === "CAMPAIGN_WHATSAPP"
                              ? "bg-violet-100 text-violet-900"
                              : "bg-sky-100 text-sky-900"
                          }`}
                        >
                          {lead.bookingChannelLabel}
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {lead.websiteOpdBookedAt
                          ? new Date(lead.websiteOpdBookedAt).toLocaleString("en-IN", {
                              timeZone: "Asia/Kolkata",
                            })
                          : "—"}
                      </td>
                      <td className="p-2 whitespace-nowrap max-w-[180px]">
                        <div>
                          {lead.websiteOpdScheduledAt
                            ? new Date(lead.websiteOpdScheduledAt).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                              })
                            : "—"}
                        </div>
                        {lead.websiteOpdDoctorName && (
                          <div className="text-[10px] text-muted-foreground truncate">
                            {lead.websiteOpdDoctorName}
                            {lead.websiteOpdHospitalName ? ` · ${lead.websiteOpdHospitalName}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="p-2 min-w-[220px]">
                        <JourneyTrail stage={lead.journeyStage} />
                        <div className="text-[10px] text-muted-foreground mt-0.5">{lead.journeyLabel}</div>
                      </td>
                      <td className="p-2">
                        {formatLeadStatusLabel(lead.currentStatus)}
                        {lead.currentSubStatus
                          ? ` · ${formatLeadSubStatusLabel(lead.currentSubStatus)}`
                          : ""}
                      </td>
                      <td className="p-2">{lead.assignedToName || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
